import { DialogModule } from '@angular/cdk/dialog';
import {
  HttpClientModule,
} from '@angular/common/http';
import {
  ErrorHandler,
  NgModule,
  Optional,
  SkipSelf,
} from '@angular/core';
import {BrowserModule, HammerModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import {
  Transition,
  TransitionService,
} from '@uirouter/angular';
import { each } from 'lodash-es';
import {
  AlertModule,
} from 'ngx-bootstrap/alert';
import {
  CollapseModule,
} from 'ngx-bootstrap/collapse';
import {
  BsDatepickerModule,
} from 'ngx-bootstrap/datepicker';
import {
  PaginationModule,
} from 'ngx-bootstrap/pagination';
import {
  TabsModule,
} from 'ngx-bootstrap/tabs';
import {
  currentBsVersion,
  setTheme,
} from 'ngx-bootstrap/utils';
import {
  ToastaConfig,
  ToastaModule,
} from 'ngx-toasta';
import {
  ApErrorHandler,
  BusinessObjectNotificationService,
} from 'webcommon/common-api';
import {
  AppRouteService,
  AprimaTranslateLoader,
  BrowserService,
  BrowserWebViewManager,
  LanguageService,
  TransitionManagerService,
  WebCommonStorageRepository,
  WebViewManagerService,
  XamarinWebViewManager,
  webcommonHttpInterceptorProviders,
} from 'webcommon/legacy-common';

import { HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';

import {BaseHammerConfig} from 'webcommon/core-ui';

const defaultToastaConfig: ToastaConfig = {
  limit: 5,
  position: 'top-right',
  showClose: true,
  showDuration: true,
  theme: 'bootstrap',
  timeout: 5000,
};

@NgModule({
  imports: [
    // For now, all these imports are only supposed to be imported a single time in
    // the app.
    // The intent is that the AppModule in the app can just import this module, and get these.
    // And this shouldn't be imported anywhere else.
    //
    // The main problem to avoid becomes obvious if BrowserModule, or some of these other modules
    // also get imported in a lazy-loaded module;
    // then an error will get thrown when navigating to that lazy-loaded module.
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AlertModule.forRoot(),
    BsDatepickerModule.forRoot(),
    CollapseModule.forRoot(),
    PaginationModule.forRoot(),
    TabsModule.forRoot(),
    ToastaModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useExisting: AprimaTranslateLoader,
      },
    }),
    HammerModule,
    // DialogModule doesn't have a forRoot() call, but it does provide some stuff (ex: Dialog class) that
    // seems reasonable to provide at the root, in case CoreUiModule is not imported
    DialogModule,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useExisting: ApErrorHandler,
    },
    {
      // This overrides the default Hammer config.
      // This may need to be moved up to the root project module if this config
      // isn't getting loaded up correctly there, but for now, it should work across all of webcommon.
      provide: HAMMER_GESTURE_CONFIG,
      useClass: BaseHammerConfig,
    },
    {
      provide: ToastaConfig,
      useValue: defaultToastaConfig,
    },
    ...webcommonHttpInterceptorProviders,
  ],
})
export class WebCommonModule {
  // This is using the same constructor pattern as BrowserModule
  constructor(
    @Optional() @SkipSelf() parentModule: WebCommonModule | null,
    private appRouteService: AppRouteService,
    private aprimaTranslateLoader: AprimaTranslateLoader,
    private browserService: BrowserService,
    private browserWebViewManager: BrowserWebViewManager,
    private businessObjectNotificationService: BusinessObjectNotificationService,
    private languageService: LanguageService,
    private transitionManagerService: TransitionManagerService,
    private transitionService: TransitionService,
    private webCommonStorageRepository: WebCommonStorageRepository,
    private webViewManagerService: WebViewManagerService,
    private xamarinWebViewManager: XamarinWebViewManager,
  ) {
    if (parentModule) {
      throw new Error(
        `WebCommonModule is already loaded. Import it only once, usually in the AppModule only`);
    }
  }

  bootstrap(): void {
    this.setupHammer();
    this.setupNotificationManagers();
    this.setupWebViewManager();
    this.setupNgxBootstrap();
    this.setupTranslations();
    this.transitionService.onSuccess({}, (t) => this.onTransitionSuccess(t));
  }


  private onTransitionSuccess(transition: Transition): void {
    // Dismiss all open modals
    // This call is only necessary if we decide to not use
    // DialogConfig.closeOnNavigation or DialogConfig.closeOnOverlayDetachments
    // this.dialog.closeAll();

    const toState = transition.targetState() || {};
    const toStateName = toState && toState.name() || '';
    const appRoute = this.appRouteService.findByStateName(toStateName);

    if (appRoute && appRoute.nativeSettings && !appRoute.nativeSettings.ctrlSetsUpPage) {
      this.webViewManagerService.setupPage(appRoute.nativeSettings);
    }
  }

  // TODO: migrate this hammer configuration stuff to use HammerGestureConfig and the hammerjs npm package
  // It wasn't done previously for these reasons:
  //    This logic can't easily be converted to options or overrides in BaseHammerConfig.ts,
  //    since this logic is changing defaults on the prototypes, which is not what the HammerGestureConfig is for.
  //    Also, the configuration options that come from HammerGestureConfig (the parent of BaseHammerConfig)
  //    only apply for Hammer instances created through the HammerGesturesPlugin,
  //    so it wouldn't affect the manual creation of instances that we are currently doing
  //    in our angularjs code, and via the angular-hammer package.
  //    This would be a problem because currently, we want this configuration below to apply in angularjs and Angular.
  //    (It is fine right now because it is applying to both angularjs and Angular,
  //    since they are both referencing the same 'Hammer' object off of the window)
  private setupHammer(): void {
    // This makes the recognizer work better on samsung galaxy devices.
    // Before this change, tap events would only fire ~10% of the time.
    // https://github.com/hammerjs/hammer.js/issues/804
    // https://github.com/hammerjs/hammer.js/issues/683
    const Hammer = (window as any).Hammer;
    Hammer.Tap.prototype.defaults.threshold = 9;
    Hammer.Tap.prototype.defaults.time = 499;

    // make hammer respect disabled attribute
    // by re-defining the handler prototype on each type of Input
    const inputHandlerKey = 'handler';
    const wrappedKey = '_' + inputHandlerKey;
    each(Hammer, (inputType) => {
      const inputPrototype = inputType.prototype;
      if (!inputPrototype || !inputPrototype[inputHandlerKey]) {
        return;
      }

      const inputHandler = inputPrototype[wrappedKey] = inputPrototype[inputHandlerKey];
      inputPrototype[inputHandlerKey] = function() {
        if (this && this.element && this.element.disabled) {
          return;
        }
        return inputHandler.apply(this, arguments);
      };
    });
  }

  private setupNotificationManagers(): void {
    this.businessObjectNotificationService.bootstrap();
  }

  private setupWebViewManager(): void {
    this.transitionManagerService.bootstrap();
    this.xamarinWebViewManager.bootstrap();
    this.webViewManagerService.loadConfiguration(this.browserWebViewManager, this.xamarinWebViewManager);
  }

  private setupNgxBootstrap(): void {
    // Set the theme of ngx-bootstrap to bs3, because we are still stuck on bs3 css.
    // In the current version of ngx-bootstrap, it was defaulting to bs4.
    // (Also, there can be a bit of a race condition for the default,
    // since the guess it makes is based on certain css being loaded on the page.
    // look at https://github.com/valor-software/ngx-bootstrap/blob/v8.0.0/src/utils/theme-provider.ts)
    // This could maybe happen in main.ts as well, I'm not sure what the difference would be
    console.log('guessed bootstrap version for ngx-bootstrap:', currentBsVersion());
    setTheme('bs3');
    console.log('manually set bootstrap version for ngx-bootstrap:', currentBsVersion());
  }

  private setupTranslations(): void {
    this.aprimaTranslateLoader.bootstrap();

    let currentLanguage = this.webCommonStorageRepository.getLang();
    if (!currentLanguage) {
      const navigator = this.browserService.getNavigator() as any;
      if (navigator.userLanguage) {
        // Explorer
        currentLanguage = navigator.userLanguage;
      } else if (navigator.language) {
        // FF
        currentLanguage = navigator.language;
      }
    }

    if (currentLanguage) {
      this.languageService.setLang(currentLanguage);
    }
  }
}
